import { useRef, useState, useEffect } from 'react';

import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from 'src/contexts/AuthContext';

import {
  Avatar,
  Box,
  Button,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Popover,
  Typography
} from '@mui/material';
import CloseButton from 'src/components/CloseButton';

import InboxTwoToneIcon from '@mui/icons-material/InboxTwoTone';
import { styled } from '@mui/material/styles';
import ExpandMoreTwoToneIcon from '@mui/icons-material/ExpandMoreTwoTone';
import AccountBoxTwoToneIcon from '@mui/icons-material/AccountBoxTwoTone';
import LockOpenTwoToneIcon from '@mui/icons-material/LockOpenTwoTone';
import AccountTreeTwoToneIcon from '@mui/icons-material/AccountTreeTwoTone';
import CloseIcon from '@mui/icons-material/Close';

const UserBoxButton = styled(Button)(
  ({ theme }) => `
    padding-left: ${theme.spacing(1)};
    padding-right: ${theme.spacing(1)};
    color: #fff !important;
    &:hover {
      color: ${theme.palette.primary.main} !important;
      background: rgba(85,105,255,0.15) !important;
    }
  `
);

const MenuUserBox = styled(Box)(
  ({ theme }) => `
        background: ${theme.colors.alpha.black[5]};
        padding: ${theme.spacing(2)};
`
);

const UserBoxText = styled(Box)(
  ({ theme }) => `
        text-align: left;
        padding-left: ${theme.spacing(1)};
`
);

const UserBoxLabel = styled(Typography)(
  ({ theme }) => `
    font-weight: ${theme.typography.fontWeightBold};
    color: ${theme.palette.mode === 'light' ? theme.colors.alpha.black[100] : '#fff'} !important;
    display: block;
  `
);

const UserBoxDescription = styled(Typography)(
  ({ theme }) => `
    color: ${theme.palette.mode === 'light' ? theme.colors.alpha.black[70] : 'rgba(255,255,255,0.7)'} !important;
  `
);


function HeaderUserbox({ userboxColor }: { userboxColor?: string }) {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const ref = useRef<any>(null);
  const [isOpen, setOpen] = useState<boolean>(false);
  // Use Supabase Auth user object for display name, avatar, and role
  let displayName = 'User';
  let avatar = '';
  let jobtitle = 'Guest';
  let initials = '';
  if (user) {
    const firstName = user.user_metadata?.first_name || '';
    const lastName = user.user_metadata?.last_name || '';
    const fullName = (firstName && lastName) ? `${firstName} ${lastName}` : '';
    if (fullName) {
      displayName = fullName;
      initials = `${firstName[0]}${lastName[0]}`.toUpperCase();
    } else if (firstName) {
      displayName = firstName;
      initials = firstName[0] ? firstName[0].toUpperCase() : '';
    } else if (user.email) {
      displayName = user.email;
      initials = displayName[0] ? displayName[0].toUpperCase() : '';
    } else {
      displayName = 'Unnamed User';
      initials = '';
    }
    // Check for avatar in app_metadata first (uploaded avatars), then fall back to user_metadata
    avatar = user.app_metadata?.avatar_url || user.user_metadata?.avatar_url || '';
    
    // Use app_metadata.role for jobtitle
    if (user.app_metadata?.role === 'admin' || (Array.isArray(user.app_metadata?.roles) && user.app_metadata.roles.includes('admin'))) {
      jobtitle = 'Admin';
    } else {
    jobtitle = user.app_metadata?.role ? user.app_metadata.role.charAt(0).toUpperCase() + user.app_metadata.role.slice(1) : 'User';
    }
  }

  const handleOpen = (): void => {
    setOpen(true);
  };

  const handleClose = (): void => {
    setOpen(false);
  };

  const handleSignOut = async (): Promise<void> => {
    try {
      await signOut();
      handleClose();
      navigate('/login');
    } catch (error) {
    }
  };

  return (
    <>
      <UserBoxButton color="secondary" ref={ref} onClick={handleOpen} sx={userboxColor ? { color: userboxColor + ' !important' } : {}}>
        <Avatar variant="rounded" alt={displayName} src={avatar || undefined}>
          {!avatar && initials}
        </Avatar>
        <Box sx={{ display: { xs: 'none', md: 'block' } }}>
          <UserBoxText>
            <UserBoxLabel variant="body1" sx={userboxColor ? { color: userboxColor + ' !important' } : {}}>{displayName}</UserBoxLabel>
            <UserBoxDescription variant="body2" sx={userboxColor ? { color: userboxColor + ' !important' } : {}}>
              {jobtitle}
            </UserBoxDescription>
          </UserBoxText>
        </Box>
        <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
          <ExpandMoreTwoToneIcon
            sx={(theme) => ({
              ml: 1,
              color: userboxColor
                ? userboxColor
                : theme.palette.mode === 'light'
                ? theme.palette.text.primary
                : undefined
            })}
          />
        </Box>
      </UserBoxButton>
      <Popover
        anchorEl={ref.current}
        onClose={handleClose}
        open={isOpen}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
        PaperProps={{
          sx: { minWidth: 280, p: 0 }
        }}
        disableScrollLock
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, pb: 1 }}>
          <Typography variant="h6">User Panel</Typography>          
          <CloseButton aria-label="close" onClick={handleClose}>
            <CloseIcon />
          </CloseButton>
        </Box>
        <Divider />
        <NavLink to="/management/profile/details" style={{ textDecoration: 'none' }}>
          <MenuUserBox
            sx={{
              minWidth: 210,
              cursor: 'pointer',
              transition: 'background 0.2s, color 0.2s',
              '&:hover': {
                background: 'rgba(85,105,255,0.15)',
                color: (theme) => `${theme.palette.primary.main} !important`
              }
            }}
            display="flex"
            onClick={handleClose}
          >
            <Avatar variant="rounded" alt={displayName} src={avatar || undefined}>
              {!avatar && initials}
            </Avatar>
            <UserBoxText>
              <UserBoxLabel variant="body1">{displayName}</UserBoxLabel>
              <UserBoxDescription variant="body2">
                {jobtitle}
              </UserBoxDescription>
            </UserBoxText>
          </MenuUserBox>
        </NavLink>
        <Divider sx={{ mb: 0 }} />
        <List sx={{ p: 1 }} component="nav">
          <ListItem disablePadding>
            <ListItemButton to="/management/profile/details" component={NavLink} onClick={handleClose}>
              <AccountBoxTwoToneIcon fontSize="small" sx={{ mr: 1 }} />
              <ListItemText primary="Profile" />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton to="/dashboards/messenger" component={NavLink} onClick={handleClose}>
              <InboxTwoToneIcon fontSize="small" sx={{ mr: 1 }} />
              <ListItemText primary="Messenger" />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton
              to="/management/profile/settings"
              component={NavLink}
              onClick={handleClose}
            >
              <AccountTreeTwoToneIcon fontSize="small" sx={{ mr: 1 }} />
              <ListItemText primary="Settings" />
            </ListItemButton>
          </ListItem>
        </List>
        <Divider sx={{ mb: 1 }} />
        {/* Hide logout if no user is logged in */}
        <List sx={{ p: 1, pt: 0 }} component="nav">
          <ListItem disablePadding>
            {user ? (
              <ListItemButton
                onClick={handleSignOut}
                sx={{
                  color: 'inherit',
                  mt: 0,
                  '&:hover': {
                    backgroundColor: 'rgba(255, 0, 0, 0.13) !important',
                    color: (theme) => `${theme.palette.error.main} !important`
                  },
                  '& .MuiSvgIcon-root': {
                    color: 'inherit'
                  }
                }}
              >
                <LockOpenTwoToneIcon fontSize="small" sx={{ mr: 1 }} />
                <ListItemText primary="Sign out" />
              </ListItemButton>
            ) : (
              <ListItemButton
                component={NavLink}
                to="/login"
                sx={{
                  color: 'inherit',
                  mt: 0,
                  '&:hover': {
                    backgroundColor: 'rgba(0, 255, 0, 0.13) !important',
                    color: (theme) => `${theme.palette.success.main} !important`
                  },
                  '& .MuiSvgIcon-root': {
                    color: 'inherit'
                  }
                }}
              >
                <AccountBoxTwoToneIcon fontSize="small" sx={{ mr: 1 }} />
                <ListItemText primary="Sign in" />
              </ListItemButton>
            )}
          </ListItem>
        </List>
      </Popover>
    </>
  );
}

export default HeaderUserbox;
